import { useMemo, useState } from 'react'
import { ChevronDown, Plus, Trash2, Wand2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/cn'
import type { FieldDefinition, SelectOption } from '@/features/form-builder/api/form-builder-types'
import { suggestMappings, fieldChoices } from '../../lib/auto-map'
import {
  defaultTransformFor,
  type ColumnMapping,
  type ColumnTransform,
  type MappingConfig,
  type WideInstanceBlock,
} from '../../lib/mapping-types'

const IGNORE = '__ignore__'

function fieldOptions(field: FieldDefinition | undefined): SelectOption[] {
  return (field?.type_config?.options as SelectOption[] | undefined) ?? []
}

export function ColumnMapper({
  headers,
  rows,
  fields,
  config,
  onConfig,
}: {
  headers: string[]
  rows: Array<Record<string, string>>
  fields: FieldDefinition[]
  config: MappingConfig
  onConfig: (c: MappingConfig) => void
}) {
  const fieldByKey = useMemo(() => new Map(fields.map((f) => [f.field_key, f])), [fields])
  // Mappable fields exclude repeatable_array (nested) — unsupported in flat CSV.
  const mappable = fields.filter((f) => f.field_type !== 'repeatable_array')

  const distinctValues = (column: string): string[] => {
    const set = new Set<string>()
    for (const r of rows) {
      const v = (r[column] ?? '').trim()
      if (v) set.add(v)
      if (set.size > 50) break
    }
    return [...set]
  }

  // ── Flat mapping (single / repeatable_long) ──
  if (config.shape !== 'repeatable_wide') {
    const setMapping = (column: string, m: ColumnMapping | null) => {
      const others = config.columns.filter((c) => c.column !== column)
      onConfig({ ...config, columns: m ? [...others, m] : others })
    }
    const mappingFor = (column: string) => config.columns.find((c) => c.column === column)

    return (
      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_auto_1.4fr] gap-2 text-xs text-muted-foreground px-1">
          <span>ستون فایل</span><span /><span>فیلد فرم</span>
        </div>
        {headers.map((h) => (
          <MappingRow
            key={h}
            column={h}
            mapping={mappingFor(h)}
            mappable={mappable}
            fieldByKey={fieldByKey}
            distinctValues={distinctValues}
            onChange={(m) => setMapping(h, m)}
          />
        ))}
      </div>
    )
  }

  // ── Wide mapping (repeatable_wide): instance blocks ──
  const setBlocks = (wideBlocks: WideInstanceBlock[]) => onConfig({ ...config, wideBlocks })

  const autoDetect = () => {
    // Group headers by an instance number embedded as `_<n>` or `@<n>`.
    const groups = new Map<number, Array<{ column: string; base: string }>>()
    for (const h of headers) {
      const m = h.match(/[_@](\d+)(?:_([^_]*))?$/)
      if (!m) continue
      const n = Number(m[1])
      // De-suffixed name for auto-mapping: drop the `_<n>` / `@<n>` segment.
      const base = h.replace(/[_@]\d+(?:_([^_]*))?$/, (_, tail) => (tail ? `_${tail}` : '')) || h
      if (!groups.has(n)) groups.set(n, [])
      groups.get(n)!.push({ column: h, base })
    }
    const choices = fieldChoices(mappable)
    const fieldType = new Map(mappable.map((f) => [f.field_key, f.field_type]))
    const blocks: WideInstanceBlock[] = [...groups.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([n, cols]) => {
        const suggestions = suggestMappings(cols.map((c) => c.base), choices)
        return {
          instanceLabel: `نمونه ${n}`,
          columns: cols.map((c) => {
            const fieldKey = suggestions[c.base] ?? ''
            return {
              column: c.column,
              fieldKey,
              transform: fieldKey
                ? defaultTransformFor(fieldType.get(fieldKey) ?? 'text')
                : ({ kind: 'text' } as ColumnTransform),
            }
          }),
        }
      })
    if (blocks.length) setBlocks(blocks)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">هر بلوک یک نمونهٔ فرم است. ستون‌های هر نمونه را به فیلدها نگاشت کنید.</p>
        <Button size="sm" variant="outline" className="gap-1 h-7 text-xs" onClick={autoDetect}>
          <Wand2 className="w-3 h-3" /> تشخیص خودکار بلوک‌ها
        </Button>
      </div>

      {config.wideBlocks.map((block, bi) => (
        <div key={bi} className="rounded-lg border border-border p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Input
              value={block.instanceLabel}
              onChange={(e) => {
                const next = [...config.wideBlocks]
                next[bi] = { ...block, instanceLabel: e.target.value }
                setBlocks(next)
              }}
              className="h-7 text-sm max-w-48"
            />
            <span className="text-xs text-muted-foreground flex-1">{block.columns.length} ستون</span>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setBlocks(config.wideBlocks.filter((_, i) => i !== bi))}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          {block.columns.map((m, ci) => (
            <MappingRow
              key={m.column + ci}
              column={m.column}
              mapping={m}
              mappable={mappable}
              fieldByKey={fieldByKey}
              distinctValues={distinctValues}
              onChange={(nm) => {
                const next = [...config.wideBlocks]
                const cols = [...block.columns]
                if (nm) cols[ci] = nm
                else cols.splice(ci, 1)
                next[bi] = { ...block, columns: cols }
                setBlocks(next)
              }}
              removable
            />
          ))}
          <AddColumnToBlock
            headers={headers}
            used={block.columns.map((c) => c.column)}
            onAdd={(col) => {
              const next = [...config.wideBlocks]
              next[bi] = { ...block, columns: [...block.columns, { column: col, fieldKey: '', transform: { kind: 'text' } }] }
              setBlocks(next)
            }}
          />
        </div>
      ))}

      <Button size="sm" variant="outline" className="gap-1" onClick={() => setBlocks([...config.wideBlocks, { instanceLabel: `نمونه ${config.wideBlocks.length + 1}`, columns: [] }])}>
        <Plus className="w-4 h-4" /> افزودن بلوک نمونه
      </Button>
    </div>
  )
}

function AddColumnToBlock({ headers, used, onAdd }: { headers: string[]; used: string[]; onAdd: (col: string) => void }) {
  const available = headers.filter((h) => !used.includes(h))
  if (available.length === 0) return null
  return (
    <Select value="" onValueChange={onAdd}>
      <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="+ افزودن ستون به این بلوک" /></SelectTrigger>
      <SelectContent>
        {available.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
      </SelectContent>
    </Select>
  )
}

function MappingRow({
  column,
  mapping,
  mappable,
  fieldByKey,
  distinctValues,
  onChange,
  removable,
}: {
  column: string
  mapping: ColumnMapping | undefined
  mappable: FieldDefinition[]
  fieldByKey: Map<string, FieldDefinition>
  distinctValues: (column: string) => string[]
  onChange: (m: ColumnMapping | null) => void
  removable?: boolean
}) {
  const [open, setOpen] = useState(false)
  const fieldKey = mapping?.fieldKey ?? ''
  const field = fieldByKey.get(fieldKey)
  const transform = mapping?.transform ?? { kind: 'text' as const }

  const selectField = (key: string) => {
    if (key === IGNORE) {
      // Flat: removes the mapping (column row still shown). Block: splices the
      // row out (re-addable via the block's column picker).
      onChange(null)
      return
    }
    const f = fieldByKey.get(key)
    onChange({ column, fieldKey: key, transform: defaultTransformFor(f?.field_type ?? 'text') })
  }

  const setTransform = (patch: Partial<ColumnTransform>) =>
    onChange({ column, fieldKey, transform: { ...transform, ...patch } })

  const isSelect = field?.field_type === 'single_select' || field?.field_type === 'multi_select'
  const isDate = field?.field_type === 'date' || field?.field_type === 'datetime'
  const hasConfig = isSelect || isDate

  return (
    <div className="rounded-lg border border-border">
      <div className="grid grid-cols-[1fr_auto_1.4fr] gap-2 items-center p-2">
        <span className="font-mono text-xs truncate px-1" title={column}>{column}</span>
        <span className="text-muted-foreground text-xs">←</span>
        <div className="flex items-center gap-1">
          <Select value={fieldKey || IGNORE} onValueChange={selectField}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={IGNORE}>— نادیده گرفتن —</SelectItem>
              {mappable.map((f) => (
                <SelectItem key={f.field_key} value={f.field_key}>{f.label?.fa || f.field_key}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasConfig && (
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setOpen((o) => !o)}>
              <ChevronDown className={cn('w-4 h-4 transition-transform', open && 'rotate-180')} />
            </Button>
          )}
        </div>
      </div>

      {open && isDate && (
        <div className="border-t border-border p-2 grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">تقویم مبدأ</label>
            <Select value={transform.sourceCalendar ?? 'jalali'} onValueChange={(v) => setTransform({ sourceCalendar: v as 'jalali' | 'gregorian' })}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="jalali">شمسی (جلالی)</SelectItem>
                <SelectItem value="gregorian">میلادی</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">قالب</label>
            <Input value={transform.sourceFormat ?? ''} onChange={(e) => setTransform({ sourceFormat: e.target.value })} placeholder="YYYY/MM/DD" className="h-8 text-sm font-mono" />
          </div>
        </div>
      )}

      {open && isSelect && (
        <ValueMapEditor
          rawValues={distinctValues(column)}
          options={fieldOptions(field)}
          valueMap={transform.valueMap ?? {}}
          multiDelimiter={field?.field_type === 'multi_select' ? (transform.multiDelimiter ?? ',') : undefined}
          onChange={(valueMap, multiDelimiter) => setTransform({ valueMap, multiDelimiter })}
        />
      )}
    </div>
  )
}

function ValueMapEditor({
  rawValues,
  options,
  valueMap,
  multiDelimiter,
  onChange,
}: {
  rawValues: string[]
  options: SelectOption[]
  valueMap: Record<string, string>
  multiDelimiter?: string
  onChange: (valueMap: Record<string, string>, multiDelimiter?: string) => void
}) {
  return (
    <div className="border-t border-border p-2 space-y-2">
      {multiDelimiter !== undefined && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">جداکنندهٔ چندمقداری</label>
          <Input value={multiDelimiter} onChange={(e) => onChange(valueMap, e.target.value)} className="h-7 w-16 text-sm font-mono" />
        </div>
      )}
      {rawValues.length === 0 ? (
        <p className="text-xs text-muted-foreground">مقدار نمونه‌ای در داده یافت نشد.</p>
      ) : (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {rawValues.map((rv) => (
            <div key={rv} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <span className="text-xs truncate px-1" title={rv}>{rv}</span>
              <span className="text-muted-foreground text-xs">←</span>
              <Select
                value={valueMap[rv] ?? '__none__'}
                onValueChange={(v) => onChange({ ...valueMap, [rv]: v === '__none__' ? rv : v }, multiDelimiter)}
              >
                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— بدون نگاشت —</SelectItem>
                  {options.map((o) => (
                    <SelectItem key={o.option_key} value={o.option_key}>{o.label?.fa || o.option_key}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
